import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface NewtonLaw {
  number: string;
  name: string;
  formula: string;
  explain: string;
  color: string;
}

interface ForceType {
  name: string;
  symbol: string;
  icon: string;
  color: string;
}

interface FamousExperiment {
  name: string;
  year: string;
  scientist: string;
  significance: string;
  color: string;
}

interface ClassicalMechanicsTemplateProps {
  topic: string;
  subtitle: string;
  newtonLaws: NewtonLaw[];
  forceTypes: ForceType[];
  experiments: FamousExperiment[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: ClassicalMechanicsTemplateProps = {
  topic: '经典力学与牛顿定律',
  subtitle: 'Classical Mechanics & Newton\'s Laws',
  newtonLaws: [
    {
      number: 'I',
      name: '惯性定律',
      formula: 'ΣF = 0 → v = const',
      explain: '不受外力或合力为零的物体，保持匀速直线运动或静止状态',
      color: '#3b82f6',
    },
    {
      number: 'II',
      name: '加速度定律',
      formula: 'F = ma',
      explain: '物体加速度的大小与合力成正比，与质量成反比',
      color: '#8b5cf6',
    },
    {
      number: 'III',
      name: '作用力与反作用力',
      formula: 'F₁₂ = -F₂₁',
      explain: '两个物体之间的作用力和反作用力大小相等、方向相反',
      color: '#ec4899',
    },
  ],
  forceTypes: [
    { name: '重力', symbol: 'G=mg', icon: '⬇️', color: '#ef4444' },
    { name: '弹力', symbol: 'F=kx', icon: '🔄', color: '#22c55e' },
    { name: '摩擦力', symbol: 'f=μN', icon: '🛑', color: '#f59e0b' },
    { name: '向心力', symbol: 'F=mv²/r', icon: '🌀', color: '#06b6d4' },
  ],
  experiments: [
    { name: '比萨斜塔实验', year: '1590', scientist: '伽利略', significance: '推翻亚里士多德"重物先落"理论', color: '#3b82f6' },
    { name: '万有引力', year: '1687', scientist: '牛顿', significance: '统一天体与地面力学', color: '#8b5cf6' },
    { name: '光速不变', year: '1905', scientist: '爱因斯坦', significance: '经典力学的边界——相对论诞生', color: '#ec4899' },
  ],
  stats: [
    { value: '1687', label: '《自然哲学的数学原理》出版' },
    { value: 'F=ma', label: '最美物理公式之一' },
    { value: '3 大定律', label: '经典力学的基石' },
  ],
  bottomQuote: '如果说我比别人看得更远，那是因为我站在巨人的肩膀上 —— 牛顿',
};

export const ClassicalMechanicsTemplate: React.FC<
  Partial<ClassicalMechanicsTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({ frame, fps, config: { damping: 18, stiffness: 80 } });

  const pendulumAngle = Math.sin(frame * 0.06) * 28;

  const lawStart = 60;
  const forceStart = 200;
  const projectileStart = 140;
  const experimentStart = 340;
  const statsStart = 440;
  const quoteStart = 490;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const projectileDraw = interpolate(frame, [projectileStart, projectileStart + 80], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const parabolaPoints: string[] = [];
  for (let t = 0; t <= 60; t++) {
    const x = 40 + t * 8.5;
    const y = 260 - (t * 5.5 - 0.09 * t * t);
    parabolaPoints.push(`${x.toFixed(1)},${y.toFixed(1)}`);
  }
  const parabolaPath = 'M' + parabolaPoints.join(' L');

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(145deg, #050510 0%, #0c0c24 50%, #08081a 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {Array.from({ length: 50 }).map((_, i) => {
        const x = (i * 211) % 1920;
        const y = (i * 163) % 1080;
        const size = 1.5 + (i % 3);
        return (
          <div
            key={`dot-${i}`}
            style={{
              position: 'absolute',
              left: x,
              top: y,
              width: size,
              height: size,
              borderRadius: '50%',
              background: '#3b82f6',
              opacity: 0.07,
            }}
          />
        );
      })}

      {/* Header */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 18}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: '#1e1b4b',
            borderRadius: 8,
            fontSize: 20,
            fontWeight: 700,
            color: '#818cf8',
            flexShrink: 0,
            border: '1px solid #818cf830',
          }}
        >
          ⚛️ 基础学科
        </div>
        <h1 style={{ fontSize: 48, fontWeight: 900, margin: 0, color: '#f1f5f9' }}>
          {p.topic}
        </h1>
        <p style={{ fontSize: 24, color: '#64748b', margin: 0, whiteSpace: 'nowrap' }}>
          {p.subtitle}
        </p>
      </div>

      {/* ===== LEFT: Newton's Three Laws ===== */}
      <div style={{ position: 'absolute', top: 130, left: 40, width: 440 }}>
        <div style={{ fontSize: 26, color: '#64748b', marginBottom: 12, letterSpacing: '0.08em', fontWeight: 600 }}>
          牛顿三大定律
        </div>
        {p.newtonLaws.map((law, i) => {
          const s = spring({
            frame: frame - (lawStart + i * 24),
            fps,
            config: { damping: 14, stiffness: 110 },
          });
          return (
            <div
              key={i}
              style={{
                background: '#0f0f23',
                borderRadius: 14,
                padding: '16px 18px',
                marginBottom: 14,
                borderLeft: `4px solid ${law.color}`,
                opacity: s,
                transform: `translateX(${(1 - s) * -40}px)`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    background: `${law.color}18`,
                    border: `2px solid ${law.color}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 22,
                    fontWeight: 900,
                    color: law.color,
                    flexShrink: 0,
                  }}
                >
                  {law.number}
                </div>
                <div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: law.color }}>
                    {law.name}
                  </div>
                  <div
                    style={{
                      fontSize: 26,
                      fontWeight: 900,
                      color: '#e2e8f0',
                      fontFamily: '"SF Mono", "Fira Code", monospace',
                      margin: '4px 0',
                    }}
                  >
                    {law.formula}
                  </div>
                </div>
              </div>
              <div style={{ fontSize: 18, color: '#94a3b8', lineHeight: 1.6, marginTop: 8 }}>
                {law.explain}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== CENTER: Projectile Motion (left) + Pendulum & forces stacked (right) ===== */}
      <div
        style={{
          position: 'absolute',
          left: 480,
          top: 140,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: 28,
        }}
      >
      <svg
        width="672"
        height="384"
        viewBox="0 0 560 320"
        style={{ flexShrink: 0 }}
      >
        <defs>
          <linearGradient id="mechProjGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.3" />
          </linearGradient>
          <marker id="mechArrow" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
            <polygon points="0,0 10,3.5 0,7" fill="#ef4444" />
          </marker>
          <marker id="mechArrowB" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
            <polygon points="0,0 10,3.5 0,7" fill="#3b82f6" />
          </marker>
        </defs>

        <text x="280" y="20" fill="#64748b" fontSize="22" textAnchor="middle" fontWeight="600">
          抛体运动轨迹 · Projectile Motion
        </text>

        {/* Ground line */}
        <line x1="30" y1="270" x2="530" y2="270" stroke="#334155" strokeWidth="2.5" />

        {/* Axis labels */}
        <text x="530" y="290" fill="#64748b" fontSize="22" textAnchor="middle">x</text>
        <text x="20" y="45" fill="#64748b" fontSize="22" textAnchor="middle">y</text>
        <line x1="35" y1="270" x2="35" y2="35" stroke="#334155" strokeWidth="2" strokeDasharray="4 4" />

        {/* Parabola path */}
        <path
          d={parabolaPath}
          fill="none"
          stroke="url(#mechProjGrad)"
          strokeWidth="4"
          strokeDasharray="800"
          strokeDashoffset={800 * (1 - projectileDraw)}
          strokeLinecap="round"
        />

        {/* Ball at current position */}
        {(() => {
          const t = projectileDraw * 60;
          const bx = 40 + t * 8.5;
          const by = 260 - (t * 5.5 - 0.09 * t * t);
          return (
            <>
              <circle cx={bx} cy={by} r={10} fill="#8b5cf6" opacity={projectileDraw > 0.05 ? 1 : 0} />
              <circle cx={bx} cy={by} r={17} fill="#8b5cf620" opacity={projectileDraw > 0.05 ? 1 : 0} />

              {projectileDraw > 0.15 && projectileDraw < 0.85 && (
                <>
                  <line x1={bx} y1={by} x2={bx} y2={by + 34} stroke="#ef4444" strokeWidth="2.5" markerEnd="url(#mechArrow)" />
                  <text x={bx + 10} y={by + 30} fill="#ef4444" fontSize="16" fontWeight="600">mg</text>
                </>
              )}
            </>
          );
        })()}

        {/* Velocity decomposition at launch */}
        {(() => {
          const vOpacity = interpolate(frame, [projectileStart + 10, projectileStart + 30], [0, 0.9], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
          return (
            <g opacity={vOpacity}>
              <line x1="40" y1="260" x2="100" y2="260" stroke="#22c55e" strokeWidth="2.5" markerEnd="url(#mechArrowB)" />
              <text x="70" y="253" fill="#22c55e" fontSize="18" fontWeight="600" textAnchor="middle">v₀ₓ</text>
              <line x1="40" y1="260" x2="40" y2="200" stroke="#06b6d4" strokeWidth="2.5" markerEnd="url(#mechArrowB)" />
              <text x="26" y="230" fill="#06b6d4" fontSize="18" fontWeight="600" textAnchor="middle">v₀ᵧ</text>
              <line x1="40" y1="260" x2="95" y2="205" stroke="#f59e0b" strokeWidth="3" markerEnd="url(#mechArrowB)" />
              <text x="78" y="218" fill="#f59e0b" fontSize="18" fontWeight="600">v₀</text>
            </g>
          );
        })()}

        {/* Height & Range labels */}
        <text
          x="280"
          y="310"
          fill="#94a3b8"
          fontSize="20"
          textAnchor="middle"
          opacity={interpolate(frame, [projectileStart + 60, projectileStart + 80], [0, 0.8], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}
        >
          R = v₀²sin2θ/g &nbsp;&nbsp;|&nbsp;&nbsp; H = v₀²sin²θ/(2g)
        </text>
      </svg>

      {/* Pendulum (top) + force diagram (bottom), to the right of projectile */}
      <svg
        width="318"
        height="510"
        viewBox="0 0 340 510"
        style={{ flexShrink: 0 }}
      >
        <defs>
          <marker id="mechArrowSide" markerWidth="12" markerHeight="8" refX="11" refY="4" orient="auto">
            <polygon points="0,0 12,4 0,8" fill="#ef4444" />
          </marker>
        </defs>

        {/* —— 单摆 —— cx=170, string length 128 */}
        <text x="170" y="24" fill="#64748b" fontSize="26" textAnchor="middle" fontWeight="600">
          单摆运动
        </text>

        <line x1="156" y1="38" x2="184" y2="38" stroke="#64748b" strokeWidth="3.5" />
        <line
          x1="170"
          y1="42"
          x2={170 + 128 * Math.sin((pendulumAngle * Math.PI) / 180)}
          y2={42 + 128 * Math.cos((pendulumAngle * Math.PI) / 180)}
          stroke="#94a3b8"
          strokeWidth="2.5"
        />
        <circle
          cx={170 + 128 * Math.sin((pendulumAngle * Math.PI) / 180)}
          cy={42 + 128 * Math.cos((pendulumAngle * Math.PI) / 180)}
          r={17}
          fill="#3b82f6"
        />
        <circle
          cx={170 + 128 * Math.sin((pendulumAngle * Math.PI) / 180)}
          cy={42 + 128 * Math.cos((pendulumAngle * Math.PI) / 180)}
          r={26}
          fill="#3b82f620"
        />

        <text
          x="170"
          y="228"
          fill="#e2e8f0"
          fontSize="24"
          textAnchor="middle"
          fontWeight="700"
          fontFamily='"SF Mono", "Fira Code", monospace'
        >
          T = 2π√(L/g)
        </text>
        <text x="170" y="260" fill="#64748b" fontSize="24" textAnchor="middle">
          周期仅与摆长和重力有关
        </text>

        <line x1="20" y1="278" x2="320" y2="278" stroke="#334155" strokeWidth="1.2" strokeDasharray="5 5" />

        <g transform="translate(0, 48)">
          {/* 标题在 N 箭杆路径之外：箭杆仅从物块顶以上开始绘制 */}
          <text x="170" y="298" fill="#64748b" fontSize="26" textAnchor="middle" fontWeight="600">
            受力分析
          </text>

          <rect x="118" y="350" width="104" height="68" rx="10" fill="#1e293b" stroke="#334155" strokeWidth="2" />
          <text x="170" y="394" fill="#94a3b8" fontSize="22" textAnchor="middle" fontWeight="700">m</text>

          {/* 物块几何中心 (170, 384)；N 箭尖高于标题下沿，箭杆避开标题占用带 */}
          <line x1="170" y1="384" x2="170" y2="450" stroke="#ef4444" strokeWidth="3" markerEnd="url(#mechArrowSide)" />
          <text x="188" y="438" fill="#ef4444" fontSize="28" fontWeight="600">mg</text>

          <line x1="170" y1="384" x2="170" y2="342" stroke="#22c55e" strokeWidth="3" />
          <polygon points="162,350 170,332 178,350" fill="#22c55e" />
          <text x="188" y="346" fill="#22c55e" fontSize="22" fontWeight="600">N</text>

          <line x1="170" y1="384" x2="268" y2="384" stroke="#3b82f6" strokeWidth="3" />
          <polygon points="258,376 276,384 258,392" fill="#3b82f6" />
          <text x="252" y="376" fill="#3b82f6" fontSize="22" fontWeight="600">F</text>

          <line x1="170" y1="384" x2="72" y2="384" stroke="#f59e0b" strokeWidth="3" />
          <polygon points="82,376 64,384 82,392" fill="#f59e0b" />
          <text x="54" y="376" fill="#f59e0b" fontSize="28" fontWeight="600">f</text>
        </g>
      </svg>
      </div>

      {/* ===== RIGHT: Force Types ===== */}
      <div style={{ position: 'absolute', top: 130, right: 40, width: 320 }}>
        <div style={{ fontSize: 26, color: '#64748b', marginBottom: 12, letterSpacing: '0.08em', fontWeight: 600 }}>
          常见力的类型
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {p.forceTypes.map((ft, i) => {
            const s = spring({
              frame: frame - (forceStart + i * 16),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  width: 'calc(50% - 6px)',
                  background: '#0f0f23',
                  borderRadius: 12,
                  padding: '14px 12px',
                  textAlign: 'center',
                  border: `1.5px solid ${ft.color}30`,
                  opacity: s,
                  transform: `scale(${0.8 + 0.2 * s})`,
                }}
              >
                <div style={{ fontSize: 32, marginBottom: 4 }}>{ft.icon}</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: ft.color }}>{ft.name}</div>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: '#e2e8f0',
                    fontFamily: '"SF Mono", "Fira Code", monospace',
                    marginTop: 4,
                  }}
                >
                  {ft.symbol}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== BOTTOM: Famous Experiments Timeline ===== */}
      <div
        style={{
          position: 'absolute',
          top: 715,
          left: 40,
          right: 40,
        }}
      >
        <div style={{ fontSize: 26, color: '#64748b', marginBottom: 12, letterSpacing: '0.08em', fontWeight: 600 }}>
          力学里程碑
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          {p.experiments.map((exp, i) => {
            const s = spring({
              frame: frame - (experimentStart + i * 20),
              fps,
              config: { damping: 14, stiffness: 110 },
            });
            return (
              <div
                key={i}
                style={{
                  flex: 1,
                  background: '#0f0f23',
                  borderRadius: 14,
                  padding: '16px 18px',
                  borderTop: `3px solid ${exp.color}`,
                  opacity: s,
                  transform: `translateY(${(1 - s) * 24}px)`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: 24, fontWeight: 900, color: exp.color }}>{exp.name}</span>
                  <span style={{ fontSize: 24, color: '#64748b', fontWeight: 600 }}>{exp.year}</span>
                </div>
                <div style={{ fontSize: 24, color: '#94a3b8', marginBottom: 4 }}>
                  👤 {exp.scientist}
                </div>
                <div style={{ fontSize: 22, color: '#cbd5e1', lineHeight: 1.5 }}>
                  {exp.significance}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== Stats ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 90,
          left: 40,
          right: 40,
          display: 'flex',
          gap: 16,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#0f0f23',
                borderRadius: 12,
                padding: '14px 10px',
                textAlign: 'center',
                border: '1px solid #1e293b',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ fontSize: 32, fontWeight: 900, color: '#818cf8' }}>{st.value}</span>
                <span style={{ fontSize: 22, color: '#64748b' }}>{st.label}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 5,          left: 40,
          right: 40,
          background: '#0f0f23',
          borderRadius: 14,
          padding: '14px 20px',
          borderTop: '3px solid #818cf8',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 24 }}>⚛️</span>
          <span style={{ fontSize: 20, color: '#e2e8f0', lineHeight: 1.5 }}>{p.bottomQuote}</span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 3,
          background: 'linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899)',
        }}
      />
    </AbsoluteFill>
  );
};
